import { useCallback, useEffect, useMemo, useRef } from 'react';
import { MatomoTagManagerContainer, MatomoTagManagerEnabled } from '@/environment';
import { AdFormData } from '@/store/ad';
import { AccountSharedFileRec } from '@netiva/classifieds-api';
import { useAppSelector } from '@/store';
import { useParams } from 'react-router-dom';

export type MatomoTrackingProps = {
  trackingCategory?: string;
  trackingAction?: string;
  trackingName?: string;
};

export type TrackAdStepParams = {
  stepIndex: number;
  stepKey?: string;
  propertyFormData?: AdFormData;
  platform?: string;
  categoryIsSuggested?: boolean;
  images?: AccountSharedFileRec[];
  documents?: AccountSharedFileRec[];
  selectedProductTrackingInfo?: string;
  selectedExtensionProductTrackingInfo?: string;
  paymentProvider?: string;
};

type AdContextRef = {
  isEnabled: boolean;
  platform?: string;
  insertionProcess?: 'create' | 'edit';
};

const hasFiles = (files?: AccountSharedFileRec[]) => (files?.length ?? 0) > 0;

const hasTutorialLink = (propertyFormData?: AdFormData) => {
  const raw = propertyFormData?.TutorialLink?.value;
  return typeof raw === 'string' && raw.trim().length > 0;
};

export const getStepTrackingAction = (stepKey?: string) => {
  switch (stepKey) {
    case 'publishing':
      return 'Step_product';
    case 'review':
      return 'Step_publish';
    default:
      return stepKey ? `Step_${stepKey}` : 'Step';
  }
};

export const useMatomoTagManager = () => {
  const isEnabled = !!MatomoTagManagerContainer && MatomoTagManagerEnabled;

  const { platform } = useAppSelector((state) => state.ad);
  const { id: idParam } = useParams();

  const isNew: boolean | undefined = useMemo(() => {
    if (idParam === '0') return true;
    return idParam ? false : undefined;
  }, [idParam]);

  const insertionProcess = isNew === undefined ? undefined : isNew ? 'create' : 'edit';

  const adContextRef = useRef<AdContextRef>({
    isEnabled,
    platform,
    insertionProcess,
  });

  useEffect(() => {
    adContextRef.current = { isEnabled, platform, insertionProcess };
  }, [isEnabled, platform, insertionProcess]);

  /** Builds a set of attributes required for Matomo tag manager click events */
  const buildMatomoTrackingAttributes = useCallback(
    ({ trackingCategory: category, trackingAction: action, trackingName: name }: MatomoTrackingProps) => {
      if (!adContextRef.current.isEnabled) return {};
      if (!category || !action) return {};

      return {
        className: 'etr-click',
        'data-etr-event-action': action,
        'data-etr-event-category': category,
        'data-etr-event-name': name ?? action,
        ...(adContextRef.current.platform
          ? { 'data-etr-event-insertion-platform': adContextRef.current.platform }
          : {}),
        ...(adContextRef.current.insertionProcess
          ? { 'data-etr-event-insertion-process': adContextRef.current.insertionProcess }
          : {}),
      } satisfies Record<string, string>;
    },
    []
  );

  /** Programatically triggers Matomo tag manager tracking event. */
  const trackMatomoEvent = useCallback(
    ({ trackingCategory: category, trackingAction: action, trackingName: name }: MatomoTrackingProps) => {
      if (!adContextRef.current.isEnabled) return;
      if (!category || !action) return;
      if (typeof window === 'undefined') return;

      window._mtm?.push({
        event: 'triggerEvent',
        dataLayer: {
          'event-category': category,
          'event-action': action,
          'event-name': name ?? action,
          ...(adContextRef.current.platform ? { 'event-insertion-platform': adContextRef.current.platform } : {}),
          ...(adContextRef.current.insertionProcess
            ? { 'event-insertion-process': adContextRef.current.insertionProcess }
            : {}),
        },
      });
    },
    []
  );

  /** Specific tracking for ad steps. */
  const getAdStepTrackingProps = useCallback((params: TrackAdStepParams): MatomoTrackingProps => {
    let trackingAction: string | undefined = getStepTrackingAction(params.stepKey);
    let trackingName: string | undefined;

    switch (params.stepKey) {
      case 'platform': {
        if (params.platform) trackingName = `${trackingAction}_${params.platform}`;
        else trackingName = trackingAction;
        break;
      }

      case 'category': {
        trackingName = `${trackingAction}_${params.categoryIsSuggested ? 'Suggestion' : 'Selfselection'}`;
        break;
      }

      case 'files': {
        const parts: string[] = [];
        if (hasFiles(params.images)) parts.push('Images');
        if (hasFiles(params.documents)) parts.push('Documents');
        if (hasTutorialLink(params.propertyFormData)) parts.push('Video');

        trackingName = parts.length ? `${trackingAction}_${parts.join('_')}` : trackingAction;
        break;
      }

      case 'publishing': {
        if (params.selectedProductTrackingInfo) {
          trackingName = `${trackingAction}_${params.selectedProductTrackingInfo}`;
          if (params.selectedExtensionProductTrackingInfo) {
            trackingName = `,${params.selectedExtensionProductTrackingInfo}`;
          }
        } else if (params.selectedExtensionProductTrackingInfo) {
          trackingName = `${trackingAction}_${params.selectedExtensionProductTrackingInfo}`;
        } else {
          trackingAction = undefined;
          trackingName = undefined;
        }
        break;
      }

      case 'review': {
        trackingName = params.paymentProvider ? `${trackingAction}_${params.paymentProvider}` : trackingAction;
        break;
      }

      default:
        trackingName = trackingAction;
    }

    return {
      trackingCategory: 'Interaction_PI-Insertion',
      trackingAction,
      trackingName,
    };
  }, []);

  const trackAdStep = useCallback(
    (params: TrackAdStepParams) => {
      const props = getAdStepTrackingProps(params);
      trackMatomoEvent(props);
    },
    [getAdStepTrackingProps, trackMatomoEvent]
  );

  return {
    isEnabled,
    buildMatomoTrackingAttributes,
    trackMatomoEvent,
    getAdStepTrackingProps,
    trackAdStep,
  };
};
